// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Erc20Abi, LYTPoolAbi, PoolDynamicAbi } from '@shared/Abis'
import { otAssert } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { PlanActionCTO, PlanActionEnum } from '@shared/PlanActionCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolDynamic } from '@typechain-types/contracts'
import { LYTPool } from '@typechain-types/contracts/LYTPool'
import { ERC20 } from '@typechain-types/index'
import { Contract, Signer } from 'ethers'
import { PlanActionClass } from './PlanActionClass'

export async function planActionExecution(planActionInteractive: PlanActionClass, signer: Signer) {
  if (!signer) {
    logOT.error('No signer')
    return
  }
  logOT.info('planActionExecution', planActionInteractive)

  switch (planActionInteractive.actionEnum) {
    // case PlanActionEnum.MintMarketMakerNonNative: {
    //   await executeReserverMintWithNonNative(planActionInteractive, signer)

    //   break
    // }
    case PlanActionEnum.ReserveNativeDeposit: {
      await executeReserveNativeDeposit(planActionInteractive, signer)
      break
    }
    // case PlanActionEnum.ReserverMintWithNonNative: {
    //   logOT.info('ReserverMintWithNonNative')

    //   break
    // }
    // case PlanActionEnum.TransferNonNativeToReserves: {
    //   logOT.info('TransferNonNativeToReserves')

    //   break
    // }
    case PlanActionEnum.TransferNativeForMarketMakerRedeem: {
      await executeTransferNativeForMarketMakerRedeem(planActionInteractive, signer)

      break
    }
    case PlanActionEnum.ProcessRedemptionForMarketMaker: {
      await executeProcessRedemptionForMarketMaker(planActionInteractive, signer)

      break
    }
    case PlanActionEnum.RequestReserveRedeem: {
      await executeRequestReserveRedeem(planActionInteractive, signer)

      break
    }

    default: {
      logOT.info('Not Implemented')
    }
  }

  planActionInteractive.completed = true
  logOT.info('planActionExecution completed', planActionInteractive.completed)
}

export const executeReserveNativeDeposit = async (
  planActionInteractive: PlanActionClass,

  signer: Signer
) => {
  const reservesWithStats = planActionInteractive.reserve

  otAssert(planActionInteractive.actionEnum === PlanActionEnum.ReserveNativeDeposit)
  logOT.info('executeReserveNativeDeposit', planActionInteractive)
  const poolAddr = reservesWithStats?.poolAddr as EthAddr
  const liquidityAssetAddr = planActionInteractive.nativeLiquidityAsset.address

  const lenderAddr: EthAddr = await signer.getAddress()
  const assets = planActionInteractive.actualAssets

  const erc20ContractSigned = new Contract(liquidityAssetAddr!, Erc20Abi, signer) as unknown as ERC20

  let currentAllowance = await erc20ContractSigned.allowance(lenderAddr, poolAddr)
  logOT.info('executeReserveNativeDeposit currentAllowance', {
    liquidityAssetAddr,
    poolAddr,
    currentAllowance,
    assets: BigInt(assets),
    diff: BigInt(assets) - currentAllowance,
  })
  if (currentAllowance < BigInt(assets)) {
    logOT.info('executeReserveNativeDeposit currentAllowance < assets', currentAllowance.toString(), assets.toString())
    const _approveReceipt = await erc20ContractSigned.approve(poolAddr, assets)

    await _approveReceipt.wait(1)
  }
  let retries = 0
  while (currentAllowance < BigInt(assets) && retries < 20) {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    currentAllowance = await erc20ContractSigned.allowance(lenderAddr, poolAddr)
    logOT.info('executeReserveNativeDeposit currentAllowance', currentAllowance.toString())
    retries++
  }

  logOT.info(
    '----------------------LYTPool executeReserveNativeDeposit 2----------------------',
    poolAddr,
    assets,
    lenderAddr,
    liquidityAssetAddr
  )
  const otPool = new Contract(poolAddr, PoolDynamicAbi, signer)
  const _depositReceipt = await otPool.deposit(assets, lenderAddr)
  await _depositReceipt.wait(1)
}

export const executeReserveNonNativeDeposit = async (
  planActionInteractive: PlanActionClass,

  signer: Signer
) => {
  const reservesWithStats = planActionInteractive.reserve
  otAssert(planActionInteractive.actionEnum === PlanActionEnum.ReserveNativeDeposit)
  logOT.info('executeReserveNonNativeDeposit', planActionInteractive)
  const poolAddr = reservesWithStats?.poolAddr as EthAddr

  const lenderAddr: EthAddr = await signer.getAddress()

  logOT.info('executeReserveNonNativeDeposit', poolAddr, lenderAddr, planActionInteractive.actualAssets)
  const poolContract = new Contract(poolAddr, PoolDynamicAbi, signer) as unknown as PoolDynamic
  const tx = await poolContract.depositOffChain(planActionInteractive.actualAssets, lenderAddr)
  await tx.wait(1)
}
export const executeTransferNonNativeToReserves = async (planActionInteractive: PlanActionCTO, signer: Signer) => {
  const { liquidityAsset } = planActionInteractive
  if (!liquidityAsset) {
    logOT.error('No liquidity asset')
    return
  }
  if (!signer) {
    logOT.error('No signer')
    return
  }
  const liquidityAssetContract = new Contract(liquidityAsset.address, Erc20Abi, signer) as unknown as ERC20
  const reserveTransferAddress = liquidityAsset.reserveTransferAddress

  const transferTx = await liquidityAssetContract.transfer(reserveTransferAddress, planActionInteractive.assets)
  await transferTx.wait(1)
}

export const executeTransferNativeForMarketMakerRedeem = async (
  planActionInteractive: PlanActionClass,
  signer: Signer
) => {
  const nativeLiquidityAsset = planActionInteractive.nativeLiquidityAsset

  logOT.info('executeTransferNativeForMarketMakerRedeem', planActionInteractive)

  if (!signer) {
    logOT.error('No signer')
    return
  }
  const liquidityAssetContract = new Contract(nativeLiquidityAsset.address, Erc20Abi, signer) as unknown as ERC20
  const lytPoolAddr = planActionInteractive.lytPoolAddr
  logOT.info('executeTransferNativeForMarketMakerRedeem', lytPoolAddr, planActionInteractive.actualAssets)
  const transferTx = await liquidityAssetContract.transfer(lytPoolAddr!, planActionInteractive.actualAssets)
  await transferTx.wait(1)
}
export const executeProcessRedemptionForMarketMaker = async (
  planActionInteractive: PlanActionClass,

  signer: Signer
) => {
  if (!signer) {
    logOT.error('No signer')
    return
  }

  const lytPoolAddr = planActionInteractive.lytPoolAddr
  logOT.info('executeProcessRedemptionForMarketMaker', {
    lytPoolAddr,
    marketMakerAddr: planActionInteractive.marketMakerAddr,
    actualAssets: planActionInteractive.actualAssets,
    actualFees: planActionInteractive.actualFees,
    redeemUuid: planActionInteractive.redeemUuid,
  })
  const lytPoolContract = new Contract(lytPoolAddr!, LYTPoolAbi, signer) as unknown as LYTPool
  const tx = await lytPoolContract.processRedemption(
    planActionInteractive.marketMakerAddr!,
    planActionInteractive.actualAssets,
    planActionInteractive.actualFees,
    planActionInteractive.redeemUuid!
  )
  await tx.wait(1)
}

export const executeRequestReserveRedeem = async (
  planActionInteractive: PlanActionClass,

  signer: Signer
) => {
  const reservesWithStats = planActionInteractive.reserve
  const reserveContract = new Contract(
    reservesWithStats?.poolAddr as EthAddr,
    PoolDynamicAbi,
    signer
  ) as unknown as PoolDynamic
  let shares = await reserveContract.convertToShares(planActionInteractive.actualAssets)
  let reconvertAssets = await reserveContract.convertToAssets(shares)
  logOT.info(
    'executeRequestReserveRedeem 1',
    'reconvertAssets',
    reconvertAssets,
    BigInt(planActionInteractive.actualAssets),
    'shares',
    shares,
    reconvertAssets < BigInt(planActionInteractive.actualAssets)
  )
  while (reconvertAssets < BigInt(planActionInteractive.actualAssets)) {
    shares = shares + 1n
    reconvertAssets = await reserveContract.convertToAssets(shares)
  }
  logOT.info('executeRequestReserveRedeem 2', 'reconvertAssets', reconvertAssets, 'shares', shares)
  const tx = await reserveContract.requestRedeem(shares)
  await tx.wait(1)
}
